import React, { useMemo, useState } from "react";
import { Avatar, Box, Chip, Paper, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import Invoices from "../../Invoices";
import Payments from "../../Payments";

const formatCurrency = (amountInCents = 0, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountInCents || 0) / 100);

const isInvoicePending = (invoice = {}) => {
  const status = String(invoice.status || "").toLowerCase();
  const remaining = Number(invoice.amount_remaining || invoice.amount_due || 0);
  return remaining > 0 && !["paid", "void", "draft"].includes(status);
};

const isInvoiceOverdue = (invoice = {}) => {
  const dueDate = Number(invoice.due_date || 0);
  return isInvoicePending(invoice) && dueDate > 0 && dueDate < Math.floor(Date.now() / 1000);
};

const isSuccessfulPayment = (payment = {}) => ["succeeded", "paid"].includes(String(payment.status || "").toLowerCase());

const BillingMetricCard = ({ color = "primary.main", icon, label, loading, value, helper }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, minHeight: 126 }}>
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar variant="rounded" sx={{ bgcolor: "rgba(25, 118, 210, 0.08)", color, height: 36, width: 36 }}>
          {icon}
        </Avatar>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850, letterSpacing: 0.45, textTransform: "uppercase" }}>
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Stack spacing={0.75}>
          <Skeleton variant="text" width="52%" height={32} />
          <Skeleton variant="text" width="74%" />
        </Stack>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 850 }}>
            {value}
          </Typography>
          {!!helper && (
            <Typography variant="body2" color="text.secondary">
              {helper}
            </Typography>
          )}
        </>
      )}
    </Stack>
  </Paper>
);

const BillingSection = ({
  invoicePage,
  invoiceRowsPerPage,
  invoices,
  loading,
  onInvoicePageChange,
  onInvoiceRowsPerPageChange,
  onPaymentPageChange,
  onPaymentRowsPerPageChange,
  paymentPage,
  paymentRowsPerPage,
  payments,
}) => {
  const [activeTab, setActiveTab] = useState("invoices");

  const metrics = useMemo(() => {
    const pendingInvoices = invoices.filter(isInvoicePending);
    const overdueInvoices = invoices.filter(isInvoiceOverdue);
    const successfulPayments = payments.filter(isSuccessfulPayment);
    const currency = invoices[0]?.currency || payments[0]?.currency || "usd";
    const amountDue = pendingInvoices.reduce((sum, row) => sum + Number(row.amount_remaining || row.amount_due || 0), 0);
    const overdue = overdueInvoices.reduce((sum, row) => sum + Number(row.amount_remaining || row.amount_due || 0), 0);
    const totalPaid = successfulPayments.reduce((sum, row) => sum + Number(row.amount_received || row.amount || 0), 0);

    return {
      amountDue,
      currency,
      overdue,
      overdueCount: overdueInvoices.length,
      pendingCount: pendingInvoices.length,
      totalPaid,
      successfulPaymentCount: successfulPayments.length,
    };
  }, [invoices, payments]);

  return (
    <Stack spacing={2} sx={{ textAlign: "left" }}>
      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 850 }}>
                Billing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track invoices, payments, and account billing health.
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="rounded" width={148} height={32} />
            ) : (
              <Chip
                label={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"} / ${payments.length} payment${payments.length === 1 ? "" : "s"}`}
                variant="outlined"
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>

        <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, p: 2, pt: 0 }}>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, mt: 2 }}>
            <BillingMetricCard
              color="warning.main"
              icon={<ReceiptLongOutlinedIcon fontSize="small" />}
              label="Amount due"
              loading={loading}
              value={formatCurrency(metrics.amountDue, metrics.currency)}
              helper={`${metrics.pendingCount} pending invoice${metrics.pendingCount === 1 ? "" : "s"}`}
            />
            <BillingMetricCard
              color="success.main"
              icon={<PaidOutlinedIcon fontSize="small" />}
              label="Total paid"
              loading={loading}
              value={formatCurrency(metrics.totalPaid, metrics.currency)}
              helper={`${metrics.successfulPaymentCount} successful payment${metrics.successfulPaymentCount === 1 ? "" : "s"}`}
            />
            <BillingMetricCard
              color={metrics.overdue > 0 ? "error.main" : "text.secondary"}
              icon={metrics.overdue > 0 ? <WarningAmberOutlinedIcon fontSize="small" /> : <AccountBalanceWalletOutlinedIcon fontSize="small" />}
              label="Overdue"
              loading={loading}
              value={formatCurrency(metrics.overdue, metrics.currency)}
              helper={metrics.overdue > 0 ? `${metrics.overdueCount} overdue invoice${metrics.overdueCount === 1 ? "" : "s"}` : "No overdue invoices"}
            />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(_event, nextValue) => setActiveTab(nextValue)}
            aria-label="Client billing sections"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value="invoices"
              label={`Invoices${loading ? "" : ` (${invoices.length})`}`}
              sx={{ fontWeight: 800, textTransform: "none" }}
            />
            <Tab
              value="payments"
              label={`Payments${loading ? "" : ` (${payments.length})`}`}
              sx={{ fontWeight: 800, textTransform: "none" }}
            />
          </Tabs>
        </Box>
        <Box sx={{ bgcolor: "grey.50", borderTop: (theme) => `1px solid ${theme.palette.divider}`, p: 2 }}>
          {activeTab === "invoices" ? (
            <Invoices
              embedded
              loading={loading}
              invoices={invoices}
              page={invoicePage}
              rowsPerPage={invoiceRowsPerPage}
              onPageChange={onInvoicePageChange}
              onRowsPerPageChange={onInvoiceRowsPerPageChange}
            />
          ) : (
            <Payments
              embedded
              loading={loading}
              payments={payments}
              page={paymentPage}
              rowsPerPage={paymentRowsPerPage}
              onPageChange={onPaymentPageChange}
              onRowsPerPageChange={onPaymentRowsPerPageChange}
            />
          )}
        </Box>
      </Paper>
    </Stack>
  );
};

export default BillingSection;
